import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classNames from "classnames";
import ReactSwitch from "react-switch";

import { Banner } from "./Banner";
import { Section1 } from "./Section1";
import { Section2 } from "./Section2";
import { Section3 } from "./Section3";
import { Section4 } from "./Section4";
import { Section1Preview } from "./Section1/Preview";
import { Section2Preview } from "./Section2/Preview";
import { Section3Preview } from "./Section3/Preview";
import { Section4Preview } from "./Section4/Preview";
import { useSelector } from "react-redux";
import Footer from "./Footer";
import { useSnapshot } from "valtio";
import { templateActions, templateStore } from "../../templateStore";

const BANNER_INDEX = 0;
const FOOTER_INDEX = 5;

interface TabConfig {
    titleKey: string;
    Component: React.FC<any>;
    hasDeviceType?: boolean;
}

interface PreviewConfig {
    Component: React.FC<any>;
    section: string;
    hasDeviceType?: boolean;
}

const TAB_CONFIG: TabConfig[] = [
    { titleKey: "Banner", Component: Banner, hasDeviceType: true },
    { titleKey: "Section 1", Component: Section1, hasDeviceType: true },
    { titleKey: "Section 2", Component: Section2, hasDeviceType: true },
    { titleKey: "Section 3", Component: Section3 },
    { titleKey: "Section 4", Component: Section4 },
    { titleKey: "Footer", Component: Footer },
];

const PREVIEW_MAP: Record<number, PreviewConfig> = {
    1: { Component: Section1Preview, section: "Section1", hasDeviceType: true },
    2: { Component: Section2Preview, section: "Section2", hasDeviceType: true },
    3: { Component: Section3Preview, section: "Section3" },
    4: { Component: Section4Preview, section: "Section4" },
};

export function CoverPage() {
    const [deviceType, setDeviceType] = React.useState<"Desktop" | "Mobile">(
        "Desktop"
    );
    const { t } = useTranslation();
    const { template } = useSnapshot(templateStore);
    const activateButton = template.SiteWeb.LaindingPage.Activer ?? false;
    const mainColor = useSelector(
        (state: any) => state.global.infoUser.maincolor
    );
    const [activeTab, setActiveTab] = React.useState<number>(0);

    const toggleNav = useCallback(
        (tab: number) => {
            if (activeTab !== tab) setActiveTab(tab);
        },
        [activeTab]
    );

    const hasPreview = activeTab !== BANNER_INDEX && activeTab !== FOOTER_INDEX;
    const showCoverPageSwitch = activeTab === BANNER_INDEX;
    const preview = PREVIEW_MAP[activeTab];

    const renderNavTabs = (
        <Nav
            tabs
            style={{
                backgroundColor: "white",
                width: "50px",
                display: "grid",
                gridTemplateColumns: `repeat(${TAB_CONFIG.length}, 1fr)`,
                border: "unset",
            }}
        >
            {TAB_CONFIG.map((tab, index) => (
                <NavItem
                    key={index}
                    className={classNames(
                        "pb-1",
                        "d-flex align-items-center text-nowrap",
                        { "disable-nav-item__clz": activeTab !== index }
                    )}
                    style={{
                        cursor: "pointer",
                        borderBottom:
                            activeTab === index ? `${mainColor} 3px solid` : "",
                    }}
                >
                    <NavLink
                        className={classNames("text-dark w-100", {
                            "active customer-account-menu-nav-link-active__clz":
                                activeTab === index,
                            "customer-account-menu-nav-link-disable__clz":
                                activeTab !== index,
                        })}
                        onClick={() => toggleNav(index)}
                    >
                        <span
                            className="px-2"
                            style={{
                                font: "normal normal 600 20px/27px Segoe UI",
                            }}
                        >
                            {t(tab.titleKey)}
                        </span>
                    </NavLink>
                </NavItem>
            ))}
        </Nav>
    );

    const renderTabContent = (
        <TabContent activeTab={activeTab} className="h-100">
            {TAB_CONFIG.map((tab, index) => (
                <TabPane tabId={index} key={index} style={{ height: "90%" }}>
                    <tab.Component
                        {...(tab.hasDeviceType && {
                            deviceType,
                            setDeviceType,
                        })}
                    />
                </TabPane>
            ))}
        </TabContent>
    );

    return (
        <div className="d-flex justify-content-between">
            <div style={{ width: hasPreview ? "50%" : "100%" }}>
                <div
                    style={{
                        borderBottom: "1px solid #ddd4d4",
                        ...(showCoverPageSwitch && {
                            display: "flex",
                            justifyContent: "space-between",
                        }),
                    }}
                >
                    {renderNavTabs}

                    {showCoverPageSwitch && (
                        <div
                            className="d-flex justify-content-between gap-3 p-2 pr-4 pt-3"
                            style={{ width: "25%" }}
                        >
                            <span
                                style={{
                                    textAlign: "left",
                                    font:
                                        "normal normal 600 16px/21px Segoe UI",
                                    letterSpacing: "0px",
                                    color: "#000000",
                                    opacity: "1",
                                }}
                            >
                                {t("Enable/Disable the cover page")}
                            </span>
                            <ReactSwitch
                                className="cac_icn_quickConnection"
                                offColor="#faafb4"
                                offHandleColor="#E30613"
                                onColor="#c2eec4"
                                onHandleColor="#34C38F"
                                height={19}
                                handleDiameter={24}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                checked={activateButton}
                                onChange={() => {
                                    templateActions.updateTemplate(
                                        [
                                            "template",
                                            "SiteWeb",
                                            "LaindingPage",
                                            "Activer",
                                        ],
                                        !activateButton
                                    );
                                }}
                            />
                        </div>
                    )}
                </div>
                {renderTabContent}
            </div>

            {hasPreview && preview && (
                <div style={{ width: "50%" }}>
                    <preview.Component
                        section={preview.section}
                        {...(preview.hasDeviceType && { deviceType })}
                    />
                </div>
            )}
        </div>
    );
}
